import { useState } from 'react'
import { CardDragHandle } from '@/shared/components/card-drag-handle'
import { Beaker, Plus, Check, X, Pencil, Trash2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  useLabTestResults,
  useLabTestDefinitions,
  useCreateLabTestResult,
  useUpdateLabTestResult,
  useDeleteLabTestResult,
  type LabTestResult,
  type LabTestDefinition,
} from '@/admin/hooks/use-lab-tasks'

interface Props {
  claimUuid: string
  taskUuid: string
  readOnly?: boolean
}

export function LabTestResultsTable({
  claimUuid,
  taskUuid,
  readOnly = false,
}: Props) {
  const { data: results = [], isLoading } = useLabTestResults(
    claimUuid,
    taskUuid
  )
  const createResult = useCreateLabTestResult(claimUuid, taskUuid)
  const [addOpen, setAddOpen] = useState(false)
  const [editResult, setEditResult] = useState<LabTestResult | null>(null)
  const updateResult = useUpdateLabTestResult(
    claimUuid,
    taskUuid,
    editResult?.uuid ?? ''
  )

  const passCount = results.filter((r) => r.pass_fail === 'PASS').length
  const failCount = results.filter((r) => r.pass_fail === 'FAIL').length

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CardDragHandle />
          <Beaker className="h-3.5 w-3.5 text-[var(--ink-3)]" />
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
            Test Results
          </span>
          {results.length > 0 && (
            <span className="text-[11px] text-[var(--ink-3)]">
              {results.length} tests
              {passCount > 0 && (
                <span className="ml-1 text-[var(--green)]">
                  · {passCount} pass
                </span>
              )}
              {failCount > 0 && (
                <span className="ml-1 text-[var(--red)]">
                  · {failCount} fail
                </span>
              )}
            </span>
          )}
        </div>
        {!readOnly && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setAddOpen(true)}
            className="h-9 rounded-full px-3.5 text-[14px] font-medium text-[var(--green)]"
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Test
          </Button>
        )}
      </div>

      {/* Results table */}
      {results.length === 0 ? (
        <div className="rounded-2xl border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-light)] px-4 py-6 text-center text-[11px] text-[var(--ink-3)]">
          {isLoading ? 'Loading…' : 'No test results recorded yet'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border-[0.5px] border-[var(--rule)]">
          <table className="w-full text-left text-[11px]">
            <thead>
              <tr className="border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
                <th className="px-3 py-2 font-medium text-[var(--ink-3)]">
                  Test
                </th>
                <th className="px-3 py-2 font-medium text-[var(--ink-3)]">
                  Standard
                </th>
                <th className="px-3 py-2 text-center font-medium text-[var(--ink-3)]">
                  Specimens
                </th>
                <th className="px-3 py-2 font-medium text-[var(--ink-3)]">
                  Set Point
                </th>
                <th className="px-3 py-2 text-right font-medium text-[var(--ink-3)]">
                  Result
                </th>
                <th className="px-3 py-2 text-right font-medium text-[var(--ink-3)]">
                  Threshold
                </th>
                <th className="px-3 py-2 text-center font-medium text-[var(--ink-3)]">
                  P/F
                </th>
                {!readOnly && (
                  <th className="px-3 py-2 text-center font-medium text-[var(--ink-3)]">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <TestResultRow
                  key={result.uuid}
                  result={result}
                  claimUuid={claimUuid}
                  taskUuid={taskUuid}
                  readOnly={readOnly}
                  onEdit={() => setEditResult(result)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add dialog */}
      {addOpen && (
        <TestResultDialog
          onClose={() => setAddOpen(false)}
          onSubmit={(data) => {
            createResult.mutate(data, { onSuccess: () => setAddOpen(false) })
          }}
          isPending={createResult.isPending}
        />
      )}

      {/* Edit dialog */}
      {editResult && (
        <TestResultDialog
          initial={editResult}
          onClose={() => setEditResult(null)}
          onSubmit={(data) => {
            updateResult.mutate(data, {
              onSuccess: () => setEditResult(null),
            })
          }}
          isPending={updateResult.isPending}
        />
      )}
    </div>
  )
}

function TestResultRow({
  result,
  claimUuid,
  taskUuid,
  readOnly,
  onEdit,
}: {
  result: LabTestResult
  claimUuid: string
  taskUuid: string
  readOnly: boolean
  onEdit: () => void
}) {
  const deleteResult = useDeleteLabTestResult(
    claimUuid,
    taskUuid,
    result.uuid
  )
  const [confirmOpen, setConfirmOpen] = useState(false)

  return (
    <tr className="border-b-[0.5px] border-[var(--rule)] last:border-b-0 hover:bg-[var(--bg-light)]">
      <td className="px-3 py-2.5">
        <div>
          <p className="font-medium text-[var(--ink)]">{result.test_name}</p>
          <p className="text-[10px] text-[var(--ink-3)]">{result.test_code}</p>
        </div>
      </td>
      <td className="px-3 py-2.5 text-[var(--ink-2)]">
        {result.standard || '-'}
      </td>
      <td className="px-3 py-2.5 text-center text-[var(--ink-2)]">
        {result.specimen_count}
      </td>
      <td className="px-3 py-2.5 text-[var(--ink-2)]">
        {result.set_point || '-'}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[var(--ink)]">
        {result.result_value != null
          ? `${result.result_value} ${result.result_unit}`
          : '-'}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[var(--ink-3)]">
        {result.pass_threshold != null
          ? `${result.pass_threshold} ${result.result_unit}`
          : '-'}
      </td>
      <td className="px-3 py-2.5 text-center">
        {result.pass_fail ? (
          <Badge
            variant={result.pass_fail === 'PASS' ? 'default' : 'destructive'}
            className={cn(
              'text-[9px]',
              result.pass_fail === 'PASS' && 'bg-[var(--green)]'
            )}
          >
            {result.pass_fail === 'PASS' ? (
              <Check className="mr-0.5 h-2.5 w-2.5" />
            ) : (
              <X className="mr-0.5 h-2.5 w-2.5" />
            )}
            {result.pass_fail}
          </Badge>
        ) : (
          <span className="text-[var(--ink-3)]">-</span>
        )}
      </td>
      {!readOnly && (
        <td className="px-3 py-2.5 text-center">
          <div className="flex items-center justify-center gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={onEdit}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteResult.isPending}
              className="text-[var(--red)] hover:text-[var(--red)]"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          <ConfirmDeleteDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Delete test result?"
            description={`The "${result.test_name || result.test_code}" test result will be permanently removed.`}
            isPending={deleteResult.isPending}
            onConfirm={() =>
              deleteResult.mutate(undefined, {
                onSuccess: () => setConfirmOpen(false),
              })
            }
          />
        </td>
      )}
    </tr>
  )
}

function TestResultDialog({
  initial,
  onClose,
  onSubmit,
  isPending,
}: {
  initial?: LabTestResult
  onClose: () => void
  onSubmit: (data: Record<string, unknown>) => void
  isPending: boolean
}) {
  const { data: definitions = [] } = useLabTestDefinitions()
  const [testCode, setTestCode] = useState(initial?.test_code ?? '')
  const [testName, setTestName] = useState(initial?.test_name ?? '')
  const [standard, setStandard] = useState(initial?.standard ?? '')
  const [specimenCount, setSpecimenCount] = useState(
    String(initial?.specimen_count ?? 1)
  )
  const [setPoint, setSetPoint] = useState(initial?.set_point ?? '')
  const [resultValue, setResultValue] = useState(
    initial?.result_value != null ? String(initial.result_value) : ''
  )
  const [resultUnit, setResultUnit] = useState(initial?.result_unit ?? '')
  const [passThreshold, setPassThreshold] = useState(
    initial?.pass_threshold != null ? String(initial.pass_threshold) : ''
  )
  const [passFail, setPassFail] = useState(initial?.pass_fail ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const handleSelectDefinition = (code: string) => {
    const def = definitions.find((d: LabTestDefinition) => d.test_code === code)
    if (!def) return
    setTestCode(def.test_code)
    setTestName(def.test_name)
    setStandard(def.standard)
    setSpecimenCount(String(def.default_specimen_count))
    setSetPoint(def.default_set_point)
    setResultUnit(def.default_unit)
    if (def.pass_threshold != null) setPassThreshold(String(def.pass_threshold))
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit' : 'Add'} Test Result</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {/* Test catalog selector */}
          {!initial && definitions.length > 0 && (
            <div>
              <Label className="text-[11px]">Select from Test Catalog</Label>
              <Select onValueChange={handleSelectDefinition}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a standardised test…" />
                </SelectTrigger>
                <SelectContent>
                  {definitions.map((d: LabTestDefinition) => (
                    <SelectItem key={d.test_code} value={d.test_code}>
                      {d.test_name} {d.standard ? `(${d.standard})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Test Code</Label>
              <Input
                value={testCode}
                onChange={(e) => setTestCode(e.target.value)}
                placeholder="e.g. STRIP_TENSILE_MD"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px]">Test Name</Label>
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="e.g. Strip Tensile (MD)"
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px]">Standard</Label>
              <Input
                value={standard}
                onChange={(e) => setStandard(e.target.value)}
                placeholder="e.g. ASTM D5147"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px]">Specimens</Label>
              <Input
                type="number"
                value={specimenCount}
                onChange={(e) => setSpecimenCount(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px]">Set Point</Label>
              <Input
                value={setPoint}
                onChange={(e) => setSetPoint(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px]">Result Value</Label>
              <Input
                type="number"
                step="0.0001"
                value={resultValue}
                onChange={(e) => setResultValue(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px]">Unit</Label>
              <Input
                value={resultUnit}
                onChange={(e) => setResultUnit(e.target.value)}
                placeholder="lbf, psi, etc."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px]">Pass Threshold</Label>
              <Input
                type="number"
                step="0.0001"
                value={passThreshold}
                onChange={(e) => setPassThreshold(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Pass/Fail</Label>
              <div className="mt-1 flex gap-2">
                {['PASS', 'FAIL', 'N/A'].map((pf) => (
                  <Button
                    key={pf}
                    type="button"
                    variant={passFail === pf ? 'default' : 'outline'}
                    size="xs"
                    onClick={() => setPassFail(pf)}
                    className={cn(
                      passFail === pf && pf === 'PASS' && 'bg-[var(--green)]',
                      passFail === pf && pf === 'FAIL' && 'bg-[var(--red)]'
                    )}
                  >
                    {pf}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                test_code: testCode,
                test_name: testName,
                standard,
                specimen_count: Number(specimenCount) || 1,
                set_point: setPoint,
                result_value: resultValue ? Number(resultValue) : null,
                result_unit: resultUnit,
                pass_threshold: passThreshold
                  ? Number(passThreshold)
                  : null,
                pass_fail: passFail,
                notes,
              })
            }
            disabled={isPending || !testCode || !testName}
          >
            {isPending ? 'Saving…' : initial ? 'Update' : 'Add Test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
